import { TruncateMessageContent } from "@/components/RichText";
import { SkeletonChatList } from "@/components/Skeletons";
import { ROUTES } from "@/constants/routes";
import { useGetChats } from "@/hooks";
import { SocketManager } from "@/services/socketService";
import { useAuthStore } from "@/store/authStore";
import useChatStore from "@/store/chatStore";
import { SOCKET_EVENTS, SocketMessageDto } from "@/types";
import { cn } from "@/utils";
import { formatDateChat } from "@/utils/formatDate";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface Props {
  filter?: {
    search?: string;
    status?: string;
    pic?: number;
  };

  displayView?: "drawer" | "list";
}

const ChatRooms: React.FC<Props> = ({ filter, displayView }) => {
  // Show/hide chat room left drawer for mobile view
  const setOpenLeftSection = useChatStore((state) => state.setOpenLeftSection);

  // selected chat room
  const { id: selectedRoomId } = useParams(); // id selected room
  const navigate = useNavigate();
  const isConnectionReady = useChatStore((state) => state.isConnectionReady);
  const roomUnreadCounts = useChatStore((state) => state.roomUnreadCounts);
  const setRoomUnreadCountsFromApi = useChatStore(
    (state) => state.setRoomUnreadCountsFromApi
  );
  const resetRoomUnreadCount = useChatStore(
    (state) => state.resetRoomUnreadCount
  );
  const incrementRoomUnreadCount = useChatStore(
    (state) => state.incrementRoomUnreadCount
  );
  const userId = useAuthStore((state) => state.user?.id);

  // Last messages object to show in list chat rooms
  const [lastMessageObj, setLastMessageObj] = useState<{
    [roomId: string]: {
      content: string;
      timestamp: string;
    };
  }>({});

  const { chatRooms, isLoading, hasNextPage, fetchNextPage, isFetching } =
    useGetChats(filter);

  // Open first chat room if not selected any room
  useEffect(() => {
    if (chatRooms?.length > 0 && !selectedRoomId) {
      // exclude room chat id in session storage
      const showEmptyDetailChat =
        sessionStorage.getItem("showEmptyDetailChat") === "1";
      if (showEmptyDetailChat) {
        return;
      }

      const roomId = chatRooms[0]?.id;
      if (roomId) {
        navigate(ROUTES.CHAT + `/${roomId}`);
      }
    }
  }, [chatRooms, selectedRoomId]);

  useEffect(() => {
    if (!selectedRoomId) return;
    sessionStorage.removeItem("showEmptyDetailChat");
  }, [selectedRoomId]);

  // Handle scroll to bottom to load more chat rooms
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isLoading || !hasNextPage || isFetching) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const paddingBottom = 16; // load more when scroll to 16px to bottom
    if (scrollHeight - scrollTop <= clientHeight + paddingBottom) {
      // Load more chat rooms
      fetchNextPage();
    }
  };

  // Update last message state
  useEffect(() => {
    if (!chatRooms) return;

    const newLastMessageObj: {
      [roomId: string]: {
        content: string;
        timestamp: string;
      };
    } = {};
    const newUnreadCounts: Record<string, number> = {};

    chatRooms.forEach((chat) => {
      const roomKey = chat.id.toString();
      newLastMessageObj[roomKey] = {
        content: getTextMessage(
          !!chat.lastMessage || !!chat.last_message_text,
          chat.last_message_text || chat.lastMessage?.message
        ),
        timestamp: chat.last_message_date || "",
      };
      newUnreadCounts[roomKey] = chat.unread_count || 0;
    });

    setLastMessageObj(newLastMessageObj);
    setRoomUnreadCountsFromApi(newUnreadCounts);
  }, [chatRooms, setRoomUnreadCountsFromApi]);

  // Get text message
  const getTextMessage = useCallback(
    (hasMessage: boolean, lastMessageText?: string) => {
      const getLastMessageContent = (lastMessageText?: string) => {
        return lastMessageText || "新規メッセージが届きました。";
      };

      // if has no message return OOOさんがチャットを作成しました。
      // else return last message text
      return hasMessage ? getLastMessageContent(lastMessageText) : "";
    },
    []
  );

  const resetUnreadForRoom = useCallback(
    (roomId: number) => {
      resetRoomUnreadCount(roomId);
    },
    [resetRoomUnreadCount]
  );

  // Listen public events to update list room chat
  useEffect(() => {
    // If connection is not ready, do not join room
    if (!isConnectionReady) return;

    let timeoutId: NodeJS.Timeout | null = null;

    // List UNHANDLED public message
    let unhandledMessages: SocketMessageDto[] = [];

    const debouncedUpdate = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout for 0.5 seconds
      timeoutId = setTimeout(() => {
        const messagesToProcess = [...unhandledMessages];
        unhandledMessages = [];

        setLastMessageObj((prev) => {
          const updated = { ...prev };

          messagesToProcess.forEach((message) => {
            const roomKey = message.roomId.toString();
            updated[roomKey] = {
              content: getTextMessage(true, message.message),
              timestamp: message.timestamp,
            };
          });

          return updated;
        });

        messagesToProcess.forEach((message) => {
          const roomKey = message.roomId.toString();
          const shouldIncrementUnread = !(
            roomKey === selectedRoomId && message.senderId === userId
          );
          if (shouldIncrementUnread) {
            incrementRoomUnreadCount(roomKey);
          }
        });

        timeoutId = null;
      }, 500); // 500 milliseconds
    };

    const handleNewPublicMessage = (data: SocketMessageDto) => {
      // Push to unhandled messages
      unhandledMessages.push(data);

      // Process unhandled messages
      debouncedUpdate();
    };

    const socketManager = SocketManager.getInstance();
    socketManager.onPublicMessage(handleNewPublicMessage);

    return () => {
      // remove event listener public message
      socketManager.off(SOCKET_EVENTS.PUBLISH_MESSAGE);
    };
  }, [isConnectionReady, selectedRoomId, userId, incrementRoomUnreadCount]);

  // Handle select room chat
  const handleSelectRoomChat = useCallback(
    (roomId: number) => {
      resetUnreadForRoom(roomId);

      navigate(`/chat-management/${roomId}`);

      // If chat rooms is drawer, close drawer
      if (displayView === "drawer") {
        setOpenLeftSection(false);
      }
    },
    [navigate, displayView, resetUnreadForRoom]
  );

  if (isLoading) return <SkeletonChatList />;

  if (!chatRooms?.length)
    return (
      <div>
        <img
          src="/svgs/empty_state_email_zero.svg"
          alt="no room chat"
          width={120}
          height={120}
          className="mx-auto mt-24"
        />
        <div className="text-center p-4 text-sm text-light_secondary">
          チャットがありません
        </div>
      </div>
    );

  return (
    <div className="overflow-auto flex-1" onScroll={handleScroll}>
      {chatRooms?.map((chat) => {
        const roomKey = chat.id.toString();
        const lastRoomMessage = lastMessageObj[roomKey];
        const unreadCount = roomUnreadCounts[roomKey] || 0;

        return (
          <div
            key={chat.id}
            className={cn(
              "p-4 hover:bg-grey-200/40 transition-colors duration-100 rounded block text-sm leading-5.5 cursor-pointer",
              selectedRoomId == String(chat.id) && "!bg-grey-200"
            )}
            onClick={() => handleSelectRoomChat(chat.id)}
          >
            {/* Chat Title  */}
            <div className="text-light_primary truncate font-semibold" title="">
              {chat.title}
            </div>

            {/* Chat last message  */}
            {/* Only show first child element, hide rest to avoid layout shift  */}
            <TruncateMessageContent
              html={lastRoomMessage?.content}
              key={chat.id}
            ></TruncateMessageContent>

            {/* Company name + time + message new status  */}
            <div className="flex items-center justify-between gap-2">
              <div
                className="text-light_secondary truncate flex-1"
                title={chat.created_by?.company?.name}
              >
                {chat.created_by?.company?.name}
              </div>
              {!!lastRoomMessage?.timestamp && (
                <div className="text-xs text-grey-600 ml-auto">
                  {formatDateChat(lastRoomMessage.timestamp)}
                </div>
              )}

              {/* Has unread message  */}
              {unreadCount > 0 && (
                <div className="size-2.5 rounded-full bg-gray_cyan"></div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChatRooms;
